import type { EstadoOrden, MotivoCancelacion } from '@/types/domain';
import type {
  AttachGuideRequest,
  CreateOrderRequest,
  OrderItemPayload,
  UpdateOrderStatusRequest,
} from '@/types/orders';

/**
 * Estado de los formularios de Órdenes y su traducción al contrato del backend.
 *
 * Igual que en Productos y Ajustes, esta capa es la única que conoce las dos formas: el formulario
 * trabaja con STRINGS (lo que devuelve un `<input>`) y con filas que llevan un `id` solo para la UI,
 * y el backend espera números y JSON normalizado. Lo que el usuario deja en blanco se descarta al
 * enviar, así que no hay que "limpiar" el formulario a mano:
 *
 *  - `emptyOrderForm` / `emptyGuideForm` → el estado con el que abre cada diálogo;
 *  - `createOrderPayload`               → body del `POST` (los opcionales vacíos se OMITEN, para que
 *    el backend aplique sus valores por defecto en vez de guardar un string vacío);
 *  - `updateOrderStatusPayload`         → body del `PATCH .../estado` (`motivo` solo si se escribió);
 *  - `attachGuidePayload`               → body del `PATCH .../guia` (archivo O URL, nunca los dos).
 */

/** Una línea del pedido en el formulario: el `id` es solo para la `key` de React. */
export interface OrderItemRow {
  id: string;
  nombre: string;
  /** Cantidad como texto (`''` = sin escribir). */
  cantidad: string;
  /** Precio unitario como texto (`''` = sin precio; acepta `,` o `.` como separador decimal). */
  precioUnitario: string;
}

export interface OrderFormState {
  clienteNombre: string;
  telefono: string;
  departamento: string;
  ciudad: string;
  barrio: string;
  direccion: string;
  metodoPago: string;
  /** Conversación de origen (opcional): al vincularla, el lead pasa a CLIENTE y a postventa. */
  conversationId: string;
  items: OrderItemRow[];
  /** Total como texto (`''` = sin escribir). El diálogo lo autocompleta desde los productos. */
  total: string;
}

/** Foto de la guía elegida en el equipo, ya leída como base64. */
export interface GuideFileState {
  /** Nombre del archivo (solo para mostrarlo en pantalla). */
  name: string;
  /** Contenido en base64 SIN el prefijo `data:` (el `mimeType` va aparte). */
  base64: string;
  mimeType: string;
  /** Tamaño en bytes (la pantalla avisa si pasa de 5 MB antes de subir). */
  size: number;
}

export interface GuideFormState {
  /** URL externa de la guía (alternativa a subir el archivo). */
  fotoUrl: string;
  /** Número de guía de la transportadora. */
  numero: string;
  /** Transportadora de la etiqueta (la lee Gemini al elegir la foto; editable). */
  transportadora: string;
  /** Número de pedido/remisión de la transportadora (viaja a postventa). */
  ordenTransportadora: string;
  /** Archivo leído en el navegador; `null` = se va a mandar la URL. */
  archivo: GuideFileState | null;
}

/**
 * Campos a los que `formErrorsFromApiError` puede atribuir un mensaje del backend.
 *
 * `productos` no es un campo del formulario: es el bloque de líneas del pedido. Los mensajes de Zod
 * llegan como `productos.0.nombre` y el reparto tolerante de `form-errors.ts` los manda a este
 * bloque, que es donde la pantalla los muestra. Los `refine` sobre el objeto completo caen en el
 * aviso general del formulario.
 */
export const ORDER_FORM_FIELDS = [
  'clienteNombre',
  'productos',
  'total',
  'telefono',
  'departamento',
  'ciudad',
  'barrio',
  'direccion',
  'metodoPago',
  'conversationId',
] as const;

/** Campos del diálogo de cambio de estado. */
export const ORDER_STATUS_FORM_FIELDS = ['estado', 'motivo'] as const;

/** Campos del diálogo de la guía. */
export const ORDER_GUIDE_FORM_FIELDS = [
  'guiaEnvioFotoUrl',
  'imageBase64',
  'guiaEnvioNumero',
  'transportadora',
  'ordenTransportadora',
] as const;

/* ---------------------------------------------------------------------------
 * Filas de la UI y lectura de los campos
 * ------------------------------------------------------------------------ */

let rowCounter = 0;

/** Id único por fila (para poder borrarlas sin que React reutilice el estado de otra). */
function nextRowId(): string {
  rowCounter += 1;
  return `item-${rowCounter}`;
}

export function emptyOrderItemRow(nombre = '', cantidad = '1', precioUnitario = ''): OrderItemRow {
  return { id: nextRowId(), nombre, cantidad, precioUnitario };
}

/** Formulario de una orden nueva: cliente sin nombre, una línea de pedido y sin total. */
export function emptyOrderForm(): OrderFormState {
  return {
    clienteNombre: '',
    telefono: '',
    departamento: '',
    ciudad: '',
    barrio: '',
    direccion: '',
    metodoPago: '',
    conversationId: '',
    items: [emptyOrderItemRow()],
    total: '',
  };
}

/** Formulario de la guía: los tres campos vacíos. */
export function emptyGuideForm(): GuideFormState {
  return { fotoUrl: '', numero: '', transportadora: '', ordenTransportadora: '', archivo: null };
}

/** Entero de un input de texto (`''` o cualquier cosa no entera → `null`). */
export function parseIntegerField(value: string): number | null {
  const text = value.trim();
  if (!/^-?\d+$/.test(text)) return null;
  return Number(text);
}

/** Monto de un input de texto: acepta `25000`, `25000.5` y `25000,5`. */
export function parseMoneyField(value: string): number | null {
  const text = value.trim().replace(',', '.');
  if (!/^-?\d+(\.\d+)?$/.test(text)) return null;

  const parsed = Number(text);
  return Number.isFinite(parsed) ? parsed : null;
}

/** ¿La fila está totalmente en blanco? (una fila así nunca llega al backend). */
function isOrderItemRowEmpty(row: OrderItemRow): boolean {
  return (
    row.nombre.trim().length === 0 &&
    row.cantidad.trim().length === 0 &&
    row.precioUnitario.trim().length === 0
  );
}

/** Cantidad de la fila, ya como número (`null` si no está escrita o no es un entero). */
function orderItemQuantity(row: OrderItemRow): number | null {
  return parseIntegerField(row.cantidad);
}

/** Precio unitario de la fila (`null` si no está escrito). */
function orderItemUnitPrice(row: OrderItemRow): number | null {
  return parseMoneyField(row.precioUnitario);
}

/**
 * `cantidad × precioUnitario` de una fila, redondeado a 2 decimales (como el `DECIMAL(12,2)` de la
 * base de datos). `null` cuando falta cualquiera de los dos: sin precio no hay subtotal que inventar.
 */
export function orderItemSubtotal(row: OrderItemRow): number | null {
  const cantidad = orderItemQuantity(row);
  const precio = orderItemUnitPrice(row);
  if (cantidad === null || precio === null) return null;

  return Math.round(cantidad * precio * 100) / 100;
}

/**
 * Suma de los subtotales de las líneas QUE SE ENVÍAN (0 si ninguna tiene precio todavía).
 *
 * Es el número del total automático del formulario: las filas a medio escribir no cuentan, porque
 * no son productos y no viajan al backend (ver `orderItemPayloads`). Así el total del input y el que
 * guarda la orden son siempre el mismo.
 */
export function orderItemsTotal(rows: OrderItemRow[]): number {
  return orderItemPayloadsSubtotal(orderItemPayloads(rows));
}

/**
 * El total como texto para el input (`''` = las líneas todavía no dan un total > 0).
 *
 * Suma las líneas que se van a enviar (no las que están a medio escribir): el campo tiene que decir
 * el mismo número que va a guardar el backend.
 */
export function totalFieldFromItems(rows: OrderItemRow[]): string {
  const subtotal = orderItemsTotal(rows);
  return subtotal > 0 ? String(subtotal) : '';
}

/* ---------------------------------------------------------------------------
 * Formulario → contrato
 * ------------------------------------------------------------------------ */

/**
 * Filas → `productos` del backend.
 *
 * Las filas vacías o a medio llenar se descartan (la validación ya avisó en pantalla: mandarlas
 * rompería el schema, que exige `nombre` y `cantidad ≥ 1`). El precio y el subtotal solo viajan
 * cuando hay precio: el contrato los acepta ausentes, y así una orden sin precios no queda con
 * subtotales en 0 que parezcan calculados.
 */
export function orderItemPayloads(rows: OrderItemRow[]): OrderItemPayload[] {
  const items: OrderItemPayload[] = [];

  for (const row of rows) {
    if (isOrderItemRowEmpty(row)) continue;

    const nombre = row.nombre.trim();
    const cantidad = parseIntegerField(row.cantidad);
    if (nombre.length === 0 || cantidad === null || cantidad < 1) continue;

    const precio = parseMoneyField(row.precioUnitario);
    const subtotal = precio === null ? null : Math.round(cantidad * precio * 100) / 100;

    items.push({
      nombre,
      cantidad,
      ...(precio !== null ? { precioUnitario: precio } : {}),
      ...(subtotal !== null ? { subtotal } : {}),
    });
  }

  return items;
}

/**
 * Suma de los subtotales que de verdad viajan al backend (`orderItemPayloads`).
 *
 * Es la que usa el total automático del formulario: sumar las filas del formulario sería sumar
 * líneas a medio escribir (una fila con precio pero sin nombre no es un producto y no se manda), y
 * el total del pedido tiene que cuadrar con lo que se guarda.
 */
export function orderItemPayloadsSubtotal(items: OrderItemPayload[]): number {
  const sum = items.reduce((total, item) => total + (item.subtotal ?? 0), 0);
  return Math.round(sum * 100) / 100;
}

/**
 * Body del `POST .../orders`.
 *
 * Los campos opcionales que quedaron vacíos NO viajan (el backend los guarda en `null`). `total`
 * sale del campo del formulario y, si no se pudo leer, de la suma de las líneas que se envían: el
 * schema lo exige (`min(0)`) y la pantalla no puede mandar un body válido sin él.
 */
export function createOrderPayload(form: OrderFormState): CreateOrderRequest {
  const productos = orderItemPayloads(form.items);
  const total = parseMoneyField(form.total) ?? orderItemPayloadsSubtotal(productos);

  const telefono = form.telefono.trim();
  const departamento = form.departamento.trim();
  const ciudad = form.ciudad.trim();
  const barrio = form.barrio.trim();
  const direccion = form.direccion.trim();
  const metodoPago = form.metodoPago.trim();
  const conversationId = form.conversationId.trim();

  return {
    clienteNombre: form.clienteNombre.trim(),
    productos,
    total,
    ...(telefono.length > 0 ? { telefono } : {}),
    ...(departamento.length > 0 ? { departamento } : {}),
    ...(ciudad.length > 0 ? { ciudad } : {}),
    ...(barrio.length > 0 ? { barrio } : {}),
    ...(direccion.length > 0 ? { direccion } : {}),
    ...(metodoPago.length > 0 ? { metodoPago } : {}),
    ...(conversationId.length > 0 ? { conversationId } : {}),
  };
}

/**
 * Body del `PATCH .../estado`.
 *
 * `motivo` solo viaja si se escribió. `motivoCancelacion` solo si se está cancelando (3-oct-2026): en
 * los demás estados mandarlo es un error del backend, y en `CANCELADA` es obligatorio.
 */
export function updateOrderStatusPayload(
  estado: EstadoOrden,
  motivo: string,
  motivoCancelacion?: MotivoCancelacion | '',
): UpdateOrderStatusRequest {
  const texto = motivo.trim();
  const cancelando = estado === 'CANCELADA';

  return {
    estado,
    ...(texto.length > 0 ? { motivo: texto } : {}),
    ...(cancelando && motivoCancelacion ? { motivoCancelacion } : {}),
  };
}

/**
 * Parte un data URL (`data:image/png;base64,iVBOR…`) en su contenido y su MIME.
 *
 * Se re-exporta desde `@/lib/image-file` porque el mismo trozo lo necesitan los dos módulos que
 * suben fotos (guía de envío y catálogo); aquí se mantiene el nombre para no romper los imports
 * de este módulo (la pantalla de la guía y su smoke lo leen de `orders-payload`).
 */
export { base64FromDataUrl } from '@/lib/image-file';

/**
 * Body del `PATCH .../guia`.
 *
 * Si hay archivo, se manda el archivo: es la opción que el admin eligió a mano, mientras que la URL
 * del campo puede haber quedado escrita de una vez anterior (el backend, si recibe las dos, prefiere
 * la URL). El número de guía solo viaja si se escribió.
 */
export function attachGuidePayload(form: GuideFormState): AttachGuideRequest {
  const numero = form.numero.trim();
  const transportadora = form.transportadora.trim();
  const orden = form.ordenTransportadora.trim();
  const fotoUrl = form.fotoUrl.trim();

  const foto: AttachGuideRequest = form.archivo
    ? { imageBase64: form.archivo.base64, mimeType: form.archivo.mimeType }
    : { guiaEnvioFotoUrl: fotoUrl };

  // Los tres textos viajan solo si se escribieron (el backend deja el valor anterior si no vienen).
  return {
    ...foto,
    ...(numero.length > 0 ? { guiaEnvioNumero: numero } : {}),
    ...(transportadora.length > 0 ? { transportadora } : {}),
    ...(orden.length > 0 ? { ordenTransportadora: orden } : {}),
  };
}

